import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { useApi, type Loadable } from '../api';
import {
  areaName,
  BASELINES,
  CONTROL_SUMMARIES,
  NATIONAL_CATALOGUE_STATUS,
  PLAIN_LANGUAGE_STATUS,
  RISK_IMPACT,
  RISK_IMPACT_STATUS,
  RISK_LABEL,
  RISK_ORDER,
} from '@grc/shared/vocabulary';
import { Card, Load, PageBack, PageHeader, RiskChip, WaveTag } from '../components';
import { ViewTabs } from '../tabs';
import { showDraftLabel } from '../config';
import { ExplainedPanel, WithPanel } from '../Explained';
import { ImplementationGuide } from '../Implementation';
import { AskNational, FirmRequests } from './AskNational';
import { CoverageSummary, ServiceCards } from './NationalCoverage';
import { GuidanceTabs } from './Policies';
import type { ControlDetail, ControlSummary, Me } from '../types';

export function GuidanceControls() {
  const state = useApi<{ controls: ControlSummary[] }>('/controls');
  const [wave, setWave] = useState('all');
  const [area, setArea] = useState('all');
  const [risk, setRisk] = useState('all');
  const [order, setOrder] = useState<'guide' | 'risk'>('guide');
  const [q, setQ] = useState('');
  const { hash } = useLocation();
  const ready = Boolean(state.data);
  useEffect(() => {
    if (ready && hash === '#maturity') document.getElementById('maturity')?.scrollIntoView();
  }, [ready, hash]);
  return (
    <>
      <PageBack to="/">Back to Home</PageBack>
      <PageHeader
        eyebrow="Guidance"
        title="Security controls"
        lede={`63 controls. ${BASELINES.btc.name}: the 56 CIS IG1 safeguards. ${BASELINES.bti.name}: the 39 in BTI Waves 1 (25) and 2 (14).`}
      />
      <GuidanceTabs />
      <WithPanel panel={<ExplainedPanel show={['risk', 'maturity', 'baselines']} />}>
        <Load state={state} what="controls">
          {({ controls }) => (
            <>
              <div className="filters" role="search">
                <label>
                  Area
                  <select value={area} onChange={(e) => setArea(e.target.value)}>
                    <option value="all">All areas</option>
                    {[...new Set(controls.map((c) => c.domainNumber))].map((d) => (
                      <option key={d} value={d}>
                        {areaName(d)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Priority
                  <select value={risk} onChange={(e) => setRisk(e.target.value)}>
                    <option value="all">All</option>
                    {(['critical', 'high', 'medium'] as const).map((l) => (
                      <option key={l} value={l}>
                        {RISK_LABEL[l]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  BTI wave
                  <select value={wave} onChange={(e) => setWave(e.target.value)}>
                    <option value="all">All controls</option>
                    <option value="1">Wave 1</option>
                    <option value="2">Wave 2</option>
                    <option value="none">Not in a wave</option>
                  </select>
                </label>
                <label>
                  Order
                  <select value={order} onChange={(e) => setOrder(e.target.value as 'guide' | 'risk')}>
                    <option value="guide">CIS order</option>
                    <option value="risk">Highest risk first</option>
                  </select>
                </label>
                <label>
                  Search
                  <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Word or number" />
                </label>
              </div>
              <ControlTable controls={controls} wave={wave} area={area} risk={risk} order={order} q={q} />
            </>
          )}
        </Load>
      </WithPanel>
    </>
  );
}

function ControlTable({ controls, wave, area, risk, order, q }: { controls: ControlSummary[]; wave: string; area: string; risk: string; order: 'guide' | 'risk'; q: string }) {
  const rows = useMemo(() => {
    const list = controls.filter(
      (c) =>
        (wave === 'all' || (wave === 'none' ? c.btiWave === null : String(c.btiWave) === wave)) &&
        (area === 'all' || c.domainNumber === area) &&
        (risk === 'all' || RISK_IMPACT[c.controlId]?.level === risk) &&
        (!q || `${c.controlId} ${c.title} ${CONTROL_SUMMARIES[c.controlId] ?? ''}`.toLowerCase().includes(q.toLowerCase())),
    );
    if (order === 'risk') list.sort((a, b) => RISK_ORDER[RISK_IMPACT[a.controlId]!.level] - RISK_ORDER[RISK_IMPACT[b.controlId]!.level]);
    return list;
  }, [controls, wave, area, risk, order, q]);
  return (
    <>
      {(showDraftLabel(PLAIN_LANGUAGE_STATUS) || showDraftLabel(RISK_IMPACT_STATUS)) && (
        <p className="notice small">Plain-language explanations and risk ratings are a draft for National IT review.</p>
      )}
      <div className="table-wrap" tabIndex={0} role="region" aria-label="Security controls">
        <table className="guidance-table">
          <caption className="sr-only">Controls, {rows.length} shown</caption>
          <thead>
            <tr>
              <th scope="col">Control</th>
              <th scope="col">Area</th>
              <th scope="col">Priority</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.controlId}>
                <td>
                  <Link to={`/guidance/controls/${c.controlId}`}>
                    <span className="cid">{c.framework === 'CIS' ? c.controlId : 'E8'}</span> {c.title}
                  </Link>
                  <div className="control-desc">{CONTROL_SUMMARIES[c.controlId] ?? ''}</div>
                  <div className="tags">
                    <WaveTag wave={c.btiWave} />
                  </div>
                </td>
                <td>{areaName(c.domainNumber, c.domain)}</td>
                <td>
                  <RiskChip controlId={c.controlId} why />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function GuidanceControl() {
  const { controlId } = useParams();
  const state = useApi<ControlDetail>(`/controls/${controlId}`);
  return (
    <Load state={state} what="the control">
      {(c) => (
        <>
          <PageBack to="/guidance">Back to guidance</PageBack>
          <PageHeader eyebrow={`${c.framework === 'CIS' ? `CIS ${c.controlId}` : 'Essential Eight'} in the ${areaName(c.domainNumber, c.domain)} area`} title={c.title} />
          <div className="tags">
            <RiskChip controlId={c.controlId} />
            <WaveTag wave={c.btiWave} />
          </div>
          <WithPanel panel={<ExplainedPanel show={['maturity', 'risk']} />}>
            <Card title="In plain words">
              <p className="lede-sm">{CONTROL_SUMMARIES[c.controlId]}</p>
              <p className="muted small">Why it matters: {RISK_IMPACT[c.controlId]?.why}</p>
            </Card>
            <ImplementationGuide controlId={c.controlId} published={{ guidance: c.guidance, successSummary: c.successSummary, benchmarkUrl: c.benchmarkUrl }} />
            <Card title="What the CIS control text says">
              <p>{c.description}</p>
              <p className="muted small">Source: {c.sourceVersion}</p>
            </Card>
          </WithPanel>
        </>
      )}
    </Load>
  );
}

/** DEC-49: the services on one tab, Ask National (with the firm's own requests) on its own. The tab is in the address. */
export type HelpTab = 'services' | 'ask';
const HELP_TABS: ReadonlyArray<{ id: HelpTab; label: string }> = [
  { id: 'services', label: 'Services' },
  { id: 'ask', label: 'Ask National' },
];

/**
 * The tab an address opens. Addresses from before the tabs keep working: "#ask" (the firm dashboard's "Ask National"
 * link) and "?ask=" (a question handed over from "Ask National about ...") open Ask National.
 */
export const helpTabOf = (tab: string | null, hash: string, ask: string | null): HelpTab =>
  tab === 'ask' || tab === 'services' ? tab : hash === '#ask' || ask ? 'ask' : 'services';

/** Service tiles, the National agent, and the firm's own requests to National IT (Phase 4C). */
export function GuidanceResources() {
  const me = useOutletContext<Loadable<Me> | undefined>();
  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();
  const firms = me?.data?.firms ?? [];
  const [sentCount, setSentCount] = useState(0);
  // A question handed over in the link (from "Ask National about ..." on a firm dashboard), for the IT lead to send.
  const [prefill] = useState(() => (params.get('ask') ? { text: params.get('ask')!.slice(0, 1000), nonce: 1 } : undefined));
  // The firm comes from the person's own group (Andres, 2026-09-24: no need to pick): the firm a link came
  // from, else the firm where they are IT lead, else their first firm.
  const current = firms.find((f) => f.firmCode === params.get('firm')) ?? firms.find((f) => f.canWrite) ?? firms[0];
  const rawTab = params.get('tab');
  const tab = helpTabOf(rawTab, hash, params.get('ask'));
  // An address from before the tabs is rewritten to name its tab, without a history entry (as Leadership does).
  useEffect(() => {
    if (rawTab !== tab) {
      const next = new URLSearchParams(window.location.search);
      next.set('tab', tab);
      setParams(next, { replace: true, preventScrollReset: true });
    }
  }, [rawTab, tab, setParams]);
  // Each tab change is a history entry, built from the live address so the firm and any handed-over question stay.
  const select = (t: HelpTab) => {
    const next = new URLSearchParams(window.location.search);
    if (next.get('tab') === t) return;
    next.set('tab', t);
    setParams(next);
  };
  // Once the handed-over question is asked, it leaves the address, so a reload or a way back does not offer it again (CP-01).
  const asked = () => {
    const next = new URLSearchParams(window.location.search);
    if (!next.has('ask')) return;
    next.delete('ask');
    setParams(next, { replace: true, preventScrollReset: true });
  };
  return (
    <>
      <PageBack to="/guidance">Back to guidance</PageBack>
      <PageHeader eyebrow="Guidance" title="How National can help" lede="The services National offers, who can use them, and a place to ask. Each firm decides what to adopt." />
      <GuidanceTabs />
      {showDraftLabel(NATIONAL_CATALOGUE_STATUS) && <p className="notice small">Draft for National IT review. Content may change before it is published.</p>}
      {/* CP-10: keys move from the tab the live address names, through the same rule (helpTabOf). */}
      <ViewTabs
        views={HELP_TABS}
        view={tab}
        onSelect={select}
        liveView={() => {
          const live = new URLSearchParams(window.location.search);
          return helpTabOf(live.get('tab'), window.location.hash, live.get('ask'));
        }}
        label="How National can help"
        idPrefix="help"
        panelId={(t) => `help-panel-${t}`}
        className="tabs-secondary"
      />
      {/* Both panels stay mounted (CP-01, as on the control page), so a conversation with National, a "Sent" note
          and an open request form survive a look at the other tab. */}
      <div id="help-panel-services" role="tabpanel" aria-labelledby="help-tab-services" tabIndex={0} className="help-panel" hidden={tab !== 'services'}>
        <CoverageSummary />
        {/* "Request details" is offered to the firm's IT lead only (DEC-49), like every request to National IT. */}
        <ServiceCards
          requestFirm={current?.canWrite ? current : undefined}
          memberOnly={Boolean(current && !current.canWrite)}
          onRequested={() => setSentCount((n) => n + 1)}
          askHref={`?${new URLSearchParams({ ...(params.get('firm') ? { firm: params.get('firm')! } : {}), tab: 'ask' }).toString()}#your-requests`}
        />
      </div>
      <div id="help-panel-ask" role="tabpanel" aria-labelledby="help-tab-ask" tabIndex={0} className="help-panel" hidden={tab !== 'ask'}>
        {me?.data && !firms.length && <p className="muted">Asking National is open to members of a firm group.</p>}
        {current && (
          <div className="ask-layout">
            <AskNational key={current.firmCode} firm={current} prefill={prefill} onSent={() => setSentCount((n) => n + 1)} onAsked={asked} />
            <FirmRequests firmCode={current.firmCode} firmName={current.displayName} nonce={sentCount} />
          </div>
        )}
      </div>
    </>
  );
}
